// 他参加者のアイコンをクリックして「その人が見ている画面と同じ画面」を追尾するフォーカスモード。
//
// 振る舞い:
// - フォーカス対象の userId が指定されている間、対象ユーザーの最新 view（pan / zoom）を
//   自分の view にリアルタイムに反映する（追尾）。
// - 自分が能動的な操作（pointerdown / keydown / wheel）を行うと即座にフォーカスモードを解除する。
// - フォーカス対象の view が無くなった場合、または対象が参加者一覧から消えた場合も解除する。
//
// 「自分で何かの操作を行う」の判定はキャンバス・ボタンを問わず document 全体で拾うため、
// アイコンクリックそのもの（フォーカス開始のクリック）は ignore-next-pointer 仕組みで除外する。

import { useEffect } from "react";
import { useCanvasStore } from "./store/canvas-store";
import { useCanvasViewersStore } from "./store/canvas-viewers-store";

export function useFocusMode(): void {
  const focusedUserId = useCanvasViewersStore((s) => s.focusedUserId);
  const setFocusedUserId = useCanvasViewersStore((s) => s.setFocusedUserId);
  const focusedView = useCanvasViewersStore((s) =>
    s.focusedUserId ? s.views[s.focusedUserId] : undefined
  );
  const setView = useCanvasStore((s) => s.setView);

  // フォーカス中: 対象ユーザーの view を即時反映して画面表示を同期する。
  // 自分の viewer.sync で view が再ブロードキャストされるが、対象ユーザーへの逆追尾は発生しない
  // （受信側で focus 対象でなければ無視されるため）。
  useEffect(() => {
    if (!focusedUserId) return;
    if (!focusedView) {
      // 対象ユーザーがビューを送ってきていない（接続直後など）。視点更新は待機する。
      return;
    }
    setView({
      panX: focusedView.panX,
      panY: focusedView.panY,
      zoom: focusedView.zoom,
    });
  }, [focusedUserId, focusedView, setView]);

  // フォーカス中、対象ユーザーの view 消失または参加者一覧からの退室を監視する。
  // 購読開始前に退室していた場合も、対象不在のフォーカス状態を残さない。
  useEffect(() => {
    if (!focusedUserId) return undefined;
    const currentState = useCanvasViewersStore.getState();
    if (currentState.focusedUserId !== focusedUserId) return undefined;
    if (!currentState.viewers[focusedUserId]) {
      setFocusedUserId(null);
      return undefined;
    }

    const unsubscribe = useCanvasViewersStore.subscribe((state, prev) => {
      if (state.focusedUserId !== focusedUserId) return;
      const viewWasRemoved = !!prev.views[focusedUserId] && !state.views[focusedUserId];
      const viewerLeft = !!prev.viewers[focusedUserId] && !state.viewers[focusedUserId];
      if (viewWasRemoved || viewerLeft) {
        setFocusedUserId(null);
      }
    });
    return () => unsubscribe();
  }, [focusedUserId, setFocusedUserId]);

  // 自分の能動操作でフォーカスモードを強制解除する。
  // 「勝手に解除される」不具合への対策として、解除条件を意図的に絞り込む:
  // - フォーカス開始のクリックや、その直後に発生する合成イベントで即座に解除されないよう、
  //   グレース時間（GRACE_MS）が経過するまでは解除判定を行わない。
  // - 修飾キー単独（Shift / Ctrl / Alt / Meta）やオートリピートの keydown では解除しない。
  //   （Shift を押しながら他者の画面を見続ける、といった操作を許容する。）
  // - wheel はごく僅かな delta（慣性スクロールの余韻など）では解除しない。
  useEffect(() => {
    if (!focusedUserId) return undefined;
    const armedAt = Date.now();
    const isArmed = (): boolean => Date.now() - armedAt >= FOCUS_RELEASE_GRACE_MS;

    const onPointerDown = (event: PointerEvent): void => {
      if (!isArmed()) return;
      // フォーカス操作 UI（参加者アバター）上のクリックは自動解除の対象外。
      // ここで解除すると、直後に発火するアバターの onClick トグルと競合して
      // フォーカス対象の「解除」「切り替え」が正しく動かなくなる。
      const target = event.target as HTMLElement | null;
      if (target?.closest("[data-focus-control]")) return;
      setFocusedUserId(null);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!isArmed()) return;
      if (event.repeat) return;
      if (MODIFIER_KEYS.has(event.key)) return;
      setFocusedUserId(null);
    };
    const onWheel = (event: WheelEvent): void => {
      if (!isArmed()) return;
      if (
        Math.abs(event.deltaX) < WHEEL_RELEASE_DELTA_THRESHOLD &&
        Math.abs(event.deltaY) < WHEEL_RELEASE_DELTA_THRESHOLD
      ) {
        return;
      }
      setFocusedUserId(null);
    };

    window.addEventListener("pointerdown", onPointerDown, { capture: true });
    window.addEventListener("keydown", onKeyDown, { capture: true });
    window.addEventListener("wheel", onWheel, { capture: true });
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, { capture: true });
      window.removeEventListener("keydown", onKeyDown, { capture: true });
      window.removeEventListener("wheel", onWheel, { capture: true });
    };
  }, [focusedUserId, setFocusedUserId]);
}

// フォーカス開始直後の合成イベントで誤解除しないためのグレース時間（ミリ秒）。
const FOCUS_RELEASE_GRACE_MS = 350;
// 慣性スクロールの余韻などで誤解除しないための wheel delta のしきい値。
const WHEEL_RELEASE_DELTA_THRESHOLD = 1;
// keydown 単独では解除しない修飾キー。
const MODIFIER_KEYS = new Set<string>(["Shift", "Control", "Alt", "Meta"]);
